<template>
  <div ref="messageList" class="message-list-container">
    <div class="message-list-prose-column">
      <!-- Prefetch tripwire: as this nears the top of the scrollport the next
           older page loads. Zero-height, so it adds nothing to the layout. -->
      <div ref="topSentinel" class="message-list__sentinel" aria-hidden="true" />
      <ConversationOlderMessagesSkeleton
        v-if="loadingOlder"
        class="message-list__older-skeleton"
      />
      <slot name="before" />
      <ul class="message-list">
        <template v-for="(slot, index) in slots" :key="slot.client_key ?? slot.id">
          <slot v-if="customEntryForSlot?.(slot)" name="timeline-entry" :message-slot="slot" />
          <ProseMessage
            v-else
            :data-message-id="slot.id"
            :class="{ 'ml-last': index === slots.length - 1 }"
            :message="projectedMessages[index]"
            :thinking-label="thinkingLabelForSlot?.(slot)"
            :character="character"
            :persona="persona"
            :timeline-messages="slotFeatures.get(slot.id)?.directorNotes === false ? emptyTimeline : (timelineMessages ?? projectedMessages)"
            :variants="slot.variants"
            :active-variant-id="slot.activeVariantId"
            :isMostRecent="mostRecentMessageId !== undefined ? String(slot.id) === String(mostRecentMessageId) : index === slots.length - 1"
            :auto-scroll-cancelled="wasAutoScrollCancelled(projectedMessages[index])"
            :read-only="readOnly"
            :actions-disabled="actionsDisabled"
            :features="slotFeatures.get(slot.id)"
            :fork-disabled="forkDisabled"
            :forking="String(forkingMessageId) === String(slot.id)"
            @edit="handleEdit(slot.id, $event)"
            @fork="handleFork(slot.id)"
            @reroll="handleReroll(slot.id)"
            @delete="handleDelete(slot.id)"
            @resend="handleResend(slot.id)"
            @director-note:update="handleDirectorNoteUpdate(slot.id, $event)"
            @director-note:delete="handleDirectorNoteDelete(slot.id)"
            @update:activeVariant="handleActiveVariant(slot.id, $event)"
            @avatar-zoom="emit('avatar-zoom', $event)"
          >
            <template v-if="$slots['message-actions']" #actions>
              <slot name="message-actions" :message-slot="slot" />
            </template>
            <template v-if="$slots['message-status']" #status>
              <slot name="message-status" :message-slot="slot" />
            </template>
          </ProseMessage>
        </template>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import ProseMessage from '@/components/chat/ProseMessage.vue'
import ConversationOlderMessagesSkeleton from '@/components/chat/ConversationOlderMessagesSkeleton.vue'
import { useMessageAutoScroll } from '@/composables/useMessageAutoScroll'
import { useLoadOlderOnScroll } from '@/composables/useLoadOlderOnScroll'
import { projectSlotToMessage, type MessageSlot, type StoreMessage, type Variant } from '@/lib/messageSlots'
import type { ChatCharacterIdentity, ChatPersonaIdentity, ChatMessageFeatures } from '@/components/chat/types'
import type { DirectorNoteDraft } from '@/features/story-conversation/directorNotes'
import type { AvatarZoomPayload } from '@/components/chat/avatarZoom'

const props = withDefaults(defineProps<{
  slots: MessageSlot[]
  timelineMessages?: StoreMessage[]
  mostRecentMessageId?: number | string | null
  character: ChatCharacterIdentity
  persona: ChatPersonaIdentity
  features?: ChatMessageFeatures
  featuresForSlot?: (slot: MessageSlot) => ChatMessageFeatures
  thinkingLabelForSlot?: (slot: MessageSlot) => string | undefined
  customEntryForSlot?: (slot: MessageSlot) => boolean
  loadingOlder?: boolean
  hasMore?: boolean
  loadOlder?: () => Promise<unknown>
  alternatives?: Record<number, Variant[]>
  actionsDisabled?: boolean
  readOnly?: boolean
  forkDisabled?: boolean
  forkingMessageId?: number | string | null
}>(), {
  slots: () => [],
  character: () => ({}),
  persona: () => ({}),
  features: () => ({}),
  loadingOlder: false,
  hasMore: false,
  alternatives: () => ({}),
  actionsDisabled: false,
  readOnly: false,
  forkDisabled: false,
  forkingMessageId: null,
})

const emit = defineEmits<{
  (event: 'edit', messageId: number | string, newContent: string): void
  (event: 'fork', messageId: number | string): void
  (event: 'reroll', messageId: number | string): void
  (event: 'delete', messageId: number | string): void
  (event: 'resend', messageId: number | string): void
  (event: 'director-note:update', messageId: number | string, noteData: DirectorNoteDraft): void
  (event: 'director-note:delete', messageId: number | string): void
  (event: 'avatar-zoom', payload: AvatarZoomPayload): void
  (event: 'variant-select', slotId: number | string, variantId: number | string): void
}>()

const messageList = ref<HTMLElement | null>(null)
const topSentinel = ref<HTMLElement | null>(null)
const emptyTimeline: StoreMessage[] = []
function sameProperties<T extends object>(a: T, b: T) {
  const keys = Object.keys(a) as (keyof T)[]
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key])
}
const projectedMessages = computed<StoreMessage[]>((previous) => {
  const byId = new Map(previous?.map((message) => [message.id, message]))
  const next = props.slots.map((slot) => {
    const message = projectSlotToMessage(slot)
    const old = byId.get(message.id)
    return old && sameProperties(old, message) ? old : message
  })
  return previous?.length === next.length && next.every((message, i) => message === previous[i]) ? previous : next
})
const slotFeatures = computed<Map<MessageSlot['id'], ChatMessageFeatures>>((previous) => new Map(props.slots.map((slot) => {
  const features = props.featuresForSlot ? { ...props.features, ...props.featuresForSlot(slot) } : props.features
  const old = previous?.get(slot.id)
  return [slot.id, old && sameProperties(old, features) ? old : features]
})))

// Hosts provide pagination; the shared list owns the scroll anchor.
useLoadOlderOnScroll({
  container: messageList,
  sentinel: topSentinel,
  hasMore: () => props.hasMore && Boolean(props.loadOlder),
  isLoading: () => props.loadingOlder,
  loadOlder: async () => { await props.loadOlder?.() },
})

// All auto-scroll behavior lives in this composable: a single one-shot,
// event-driven scroll per new message, with no "follow" behavior.
const { wasAutoScrollCancelled } = useMessageAutoScroll({
  container: messageList,
  getMessages: () => projectedMessages.value,
  getAlternatives: () => props.alternatives,
})

// Handlers
const handleEdit = (messageId: number | string, newContent: string) => emit('edit', messageId, newContent)
const handleFork = (messageId: number | string) => emit('fork', messageId)
const handleReroll = (messageId: number | string) => emit('reroll', messageId)
const handleDelete = (messageId: number | string) => emit('delete', messageId)
const handleResend = (messageId: number | string) => emit('resend', messageId)
const handleDirectorNoteUpdate = (messageId: number | string, noteData: DirectorNoteDraft) => emit('director-note:update', messageId, noteData)
const handleDirectorNoteDelete = (messageId: number | string) => emit('director-note:delete', messageId)
const handleActiveVariant = (slotId: number | string, variantId: number | string) => {
  emit('variant-select', slotId, variantId)
}
</script>

<style scoped>
.message-list-container {
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
  overscroll-behavior: auto;
  /* Reserve the measured floating composer band so content can scroll behind it. */
  padding-bottom: var(--composer-band-height, 96px);
  /* Query container so the last-message spacer can size to the viewport (cqh). */
  container-type: size;
}

/*
 * Spacer under the newest message: reserves ~one viewport of room below the
 * last message so it can always be scrolled to the top of the view (and so a
 * streaming reply fills empty space instead of pushing the page). The room
 * persists after completion by design.
 */
.message-list > :global(.ml-last) {
  min-height: calc(100cqh - 16px);
}

.message-list-prose-column {
  max-width: 720px;
  margin: 0 auto;
  padding-top: 16px;
  padding-right: var(--conversation-side-padding, 16px);
  padding-left: var(--conversation-side-padding, 16px);
}

.message-list__sentinel {
  height: 0;
  margin: 0;
  pointer-events: none;
}

/* Match the inter-message rhythm so the placeholder reads as part of the list. */
.message-list__older-skeleton {
  margin-bottom: 16px;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  list-style: none;
}

</style>
